import { useLazyQuery } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Button, Modal, Text } from '~/components/blocks';
import { GetOrganizationBusinessHoursForAnnounceModalDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { announceOrganizationBusinessHourModalState } from '~/state/partials/announce_organization_business_hour_modal/atoms';
import { editSettingDrawerState } from '~/state/settings_organization_webbookingsettings/atoms';

export const AnnounceOrganizationBusinessHourModal = () => {
  const { afterLogin } = useAtomValue(announceOrganizationBusinessHourModalState);
  const resetState = useResetAtom(announceOrganizationBusinessHourModalState);
  const setDrawerState = useSetAtom(editSettingDrawerState);
  const [isOpen, setIsOpen] = useState(false);
  const { verifyRole, loadingRole } = usePractitionerRole();
  const router = useRouter();

  const [fetchOrganizationBusinessHours, { data, error: fetchError }] = useLazyQuery(
    GetOrganizationBusinessHoursForAnnounceModalDocument,
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (!afterLogin) return;
    if (loadingRole) return;

    if (!verifyRole('admin')) {
      resetState();
      return;
    }

    fetchOrganizationBusinessHours();
  }, [afterLogin, loadingRole, resetState, verifyRole, fetchOrganizationBusinessHours]);

  useEffect(() => {
    if (!fetchError) return;
    resetState();
  }, [fetchError, resetState]);

  useEffect(() => {
    if (!data) return;

    const organization = getMe(data)?.organization;
    const businessHours = organization?.organizationBusinessHours ?? [];
    const published = organization?.webBookingsSetting?.published ?? false;
    if (businessHours.length > 0 || !published) {
      resetState();
      return;
    }

    setIsOpen(true);
  }, [data, resetState]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, [resetState]);

  const handleClick = useCallback(async () => {
    setIsOpen(false);
    resetState();
    setDrawerState({ isOpen: true, shouldScrollToBusinessHours: true });
    await router.push('/settings/organization/web_booking_settings');
  }, [resetState, router, setDrawerState]);

  return (
    <Modal open={isOpen} onClose={handleClose} size="s">
      <Modal.Header>
        <Text size="l" fontWeight="bold">
          公開情報ページにて営業時間の設定を完了さ
          <br />
          せてください
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Alert status="info">
          <Text>
            営業時間の設定方法が変更されました。
            <br />
            設定ページをご確認のうえ、保存してください。
            <br />
            変更がない場合も、保存すると本メッセージは表示されなくなります。
          </Text>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button use="base" onClick={handleClick}>
          公開情報ページにアクセス
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
